// useRogueTestData — client-only isolated test-batch store for the
// RogueSamplingSummary panel.
//
// CRITICAL — STRUCTURAL ISOLATION FROM THE REAL SAMPLING STORE.
// This module never touches /api/admin/electionday-sampling and
// never POSTS / PUTs anywhere. Test batches live in localStorage on
// the operator's browser and feed the panel via a render-time
// override. Public readers' RogueSamplingSummary instances never see
// these batches. This mirrors the results_demo_mode discipline: test
// data structurally separate from the real public state, never
// merged, loudly labelled.
//
// Usage:
//   const { batches, setBatches, addBatch, clear, isActive } =
//     useRogueTestData();
//
//   <RogueSamplingSummary
//     testBatches={isActive ? batches : null}
//     testMode={isActive}
//   />
//
// The panel's `testBatches` prop, when non-null, replaces the live
// public sampling state for that render only.

import { useCallback, useEffect, useState } from 'react';
import type { Batch } from '../components/ElectionDay/stats';

const TEST_BATCHES_KEY = 'lm.rogue-test-batches';
const TEST_ACTIVE_KEY = 'lm.rogue-test-active';

// Same shape the panel reads; intentionally identical to the wire
// type so any future Scope B field additions slot in without a
// migration step here.
function readStoredBatches(): Batch[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(TEST_BATCHES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((b): b is Batch =>
        b &&
        typeof b === 'object' &&
        typeof b.district === 'string' &&
        typeof b.pl === 'number' &&
        typeof b.pn === 'number' &&
        typeof b.abstained === 'number',
      )
      .map((b) => ({
        district: b.district,
        pl: Math.max(0, Math.floor(b.pl)),
        pn: Math.max(0, Math.floor(b.pn)),
        abstained: Math.max(0, Math.floor(b.abstained)),
      }));
  } catch {
    return [];
  }
}

function writeStoredBatches(b: Batch[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TEST_BATCHES_KEY, JSON.stringify(b));
  } catch {
    /* ignore quota / private-mode */
  }
}

function readActive(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(TEST_ACTIVE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeActive(v: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (v) window.localStorage.setItem(TEST_ACTIVE_KEY, '1');
    else window.localStorage.removeItem(TEST_ACTIVE_KEY);
  } catch {
    /* ignore */
  }
}

// Custom event so multiple useRogueTestData() consumers on the same
// page (e.g. the admin preview surface AND the panel) update in
// lockstep without an SWR-style hub. Keeps the API tiny.
const CHANGE_EVT = 'lm:rogue-test-change';
function emit(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVT));
  } catch {
    /* ignore */
  }
}

export interface UseRogueTestDataResult {
  batches: Batch[];
  isActive: boolean;
  setActive: (v: boolean) => void;
  setBatches: (b: Batch[]) => void;
  addBatch: (b: Batch) => void;
  clear: () => void;
  /** Bulk-load a synthetic dataset — one of the named ladder rungs
   *  ('insufficient' / 'directional' / 'projected_pl' /
   *  'projected_pn'). Also activates test mode. */
  loadPreset: (name: PresetName) => void;
}

export type PresetName =
  | 'insufficient'
  | 'directional_pl'
  | 'directional_pn'
  | 'projected_pl'
  | 'projected_pn';

export function useRogueTestData(): UseRogueTestDataResult {
  const [batches, setBatchesState] = useState<Batch[]>(() => readStoredBatches());
  const [isActive, setIsActiveState] = useState<boolean>(() => readActive());

  useEffect(() => {
    const onChange = () => {
      setBatchesState(readStoredBatches());
      setIsActiveState(readActive());
    };
    window.addEventListener(CHANGE_EVT, onChange);
    // Also pick up cross-tab updates so two browser windows on the
    // same admin surface stay consistent.
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CHANGE_EVT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const setBatches = useCallback((b: Batch[]) => {
    writeStoredBatches(b);
    setBatchesState(b);
    emit();
  }, []);

  const addBatch = useCallback(
    (b: Batch) => {
      const next = [...readStoredBatches(), b];
      writeStoredBatches(next);
      setBatchesState(next);
      emit();
    },
    [],
  );

  const setActive = useCallback((v: boolean) => {
    writeActive(v);
    setIsActiveState(v);
    emit();
  }, []);

  const clear = useCallback(() => {
    writeStoredBatches([]);
    writeActive(false);
    setBatchesState([]);
    setIsActiveState(false);
    emit();
  }, []);

  const loadPreset = useCallback((name: PresetName) => {
    const next = PRESETS[name]();
    writeStoredBatches(next);
    writeActive(true);
    setBatchesState(next);
    setIsActiveState(true);
    emit();
  }, []);

  return { batches, isActive, setActive, setBatches, addBatch, clear, loadPreset };
}

// ── Synthetic presets ───────────────────────────────────────────
//
// Each preset returns a Batch[] tuned to land the certainty ladder
// at exactly one rung. They're approximate — once you load one,
// you can use addBatch() / setBatches() to push past it. Real
// thresholds (see stats.ts): MIN_CLEAN_DIRECTIONAL=150,
// MIN_CLEAN_PROJECTED=800, MIN_DISTRICTS_COVERED=7,
// PROJECTED_MIN_GAP=0.045 (~9pp).

const DISTRICTS_FULL: string[] = Array.from({ length: 13 }, (_, i) => `District ${i + 1}`);

function batch(district: string, pl: number, pn: number, abstained = 0): Batch {
  return { district, pl, pn, abstained };
}

const PRESETS: Record<PresetName, () => Batch[]> = {
  // Below the directional floor — small sample spread across 4 districts.
  // Should land at INSUFFICIENT.
  insufficient: () => [
    batch('District 1', 12, 9, 2),
    batch('District 5', 18, 15, 3),
    batch('District 8', 7, 11, 1),
    batch('District 11', 14, 13, 2),
  ],
  // Above the directional threshold (≥150 clean, ≥7 districts) but
  // not the projected one. PL ahead. Should land at DIRECTIONAL.
  directional_pl: () =>
    DISTRICTS_FULL.slice(0, 7).map((d, i) =>
      batch(d, 18 + i, 11 + (i % 3), 4 + (i % 2)),
    ),
  directional_pn: () =>
    DISTRICTS_FULL.slice(0, 7).map((d, i) =>
      batch(d, 11 + (i % 3), 18 + i, 4 + (i % 2)),
    ),
  // Above the projected threshold (≥800 clean, ≥7 districts, ≥4.5pp
  // gap). PL ahead. Should land at PROJECTED — PL.
  projected_pl: () =>
    DISTRICTS_FULL.map((d, i) =>
      batch(d, 60 + (i % 4), 38 + (i % 3), 10 + (i % 5)),
    ),
  projected_pn: () =>
    DISTRICTS_FULL.map((d, i) =>
      batch(d, 38 + (i % 3), 60 + (i % 4), 10 + (i % 5)),
    ),
};
